import { toastApp } from '@/components/toast-message/sonner';
import { HistoryTradeItem } from '@/hooks/useGetHistory';
import { percentage } from '@/hooks/useGetPosition';
import { useAddress } from '@/store/selector';
import { formatDisplay } from '@/utils/format-display';
import BigNumber from 'bignumber.js';
import SharePnl from '@/components/SharePnl/SharePnl';
import { numberFormat } from '@/utils/numberFormat';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import { useNavigate } from 'react-router';
import { useBalance } from '@/hooks/useBalance';
import { OpenTrade } from '@/components/trading/open-trade';
import { OrderType, Type } from '@/components/trading/type';
import {
    CachedIcon,
    CloseIcon,
    ReceiptLongIcon,
    // TrendingUpIcon,
} from '@/icons';
import { Spinner } from '@/components/spinner';
type Props = {
    item: HistoryTradeItem;
    isOpen: boolean;
    isClose: boolean;
    onClose: () => void;
};

export default function SheetDetail({ item, isOpen, isClose, onClose }: Props) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data: usdcBalance } = useBalance();

    const [openSharePnl, setIsOpenSharePnl] = useState(false);

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);

    const handleReopenOrder = async (isBuy: boolean) => {
        if (
            new BigNumber(usdcBalance?.toString() ?? '0').lt(
                item.collateralBeforeFee
            )
        ) {
            return toastApp.error(t('Not enough balance'));
        }

        setIsLoading(true);
        await OpenTrade({
            address,
            buy: isBuy,
            onSuccess: () => {
                onClose();
                navigate('/orders?tab=Position', {
                    state: { isOpened: true },
                });
            },
        })({
            pairIndex: item.pairIndex.toString(),
            leverage: item.leverage,
            amount: new BigNumber(item.collateralBeforeFee)
                .dividedBy(1e18)
                .toString(),
            orderType: OrderType.Market,
            tp: '',
            sl: '',
            type: isBuy ? Type.Long : Type.Short,
            limitPrice: '',
        });

        setIsLoading(false);
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content">
            <Sheet.Container className="">
                <Sheet.Header />
                <Sheet.Content className="">
                    <div className="flex flex-col mt-auto px-6 pb-4 mb-6">
                        <button
                            onClick={(e) => {
                                onClose();
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            className="absolute top-0 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b] hover:bg-gray-200 transition-colors"
                        >
                            <CloseIcon className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="relative w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center border border-[#E6E8EC] shrink-0">
                                <img
                                    className="w-full h-full object-cover"
                                    src={item.logoUrl}
                                    alt={item.symbol}
                                />
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <div className="font-bold text-base tracking-tight leading-tight">
                                        {item.nameDisplay}
                                    </div>
                                    <div className="flex gap-1">
                                        <span
                                            className={clsx(
                                                'text-xs font-semibold px-2 py-0.5 rounded-full ml-1',
                                                {
                                                    'text-[#00C076] bg-[#00C076]/10':
                                                        item.buy,
                                                    'text-[#FF4D4F] bg-[#FF4D4F]/10':
                                                        !item.buy,
                                                }
                                            )}
                                        >
                                            {item.buy ? 'Long' : 'Short'}
                                        </span>
                                        <span className="text-xs font-medium text-[#64748b] px-2 py-0.5 rounded-full bg-[#f1f5f9]">
                                            {item.leverage}x
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-4">
                            <div className="flex justify-between items-center ">
                                <p className="text-sm text-[#64748b]">
                                    {t('Use')}
                                </p>

                                <p className="font-semibold text-slate-900">
                                    {numberFormat(
                                        Number(item.initialPosToken) /
                                            Math.pow(10, item.decimals)
                                    )}{' '}
                                    WLD
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-sm text-[#64748b]">
                                    {t('Leverage')}
                                </p>

                                <p className="font-semibold text-slate-900">
                                    {item.leverage}X
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-sm text-[#64748b]">
                                    {t('Position')}
                                </p>

                                <p className="font-semibold text-slate-900">
                                    {formatDisplay(
                                        (Number(item.initialPosToken) /
                                            Math.pow(10, 18)) *
                                            item.leverage,
                                        {
                                            decimalToShow: 2,
                                        }
                                    )}{' '}
                                    WLD
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-sm text-[#64748b]">
                                    {t('Entry Price')}
                                </p>

                                <p className="font-semibold text-slate-900">
                                    {formatDisplay(
                                        Number(item.openPrice) /
                                            Math.pow(10, 10),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}{' '}
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-sm text-[#64748b]">
                                    {t('Close Price')}
                                </p>

                                <p className="font-semibold text-slate-900">
                                    {formatDisplay(
                                        Number(item.closePrice) /
                                            Math.pow(10, 10),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}
                                </p>
                            </div>

                            <div className="bg-[#f1f5f9] rounded-2xl p-4 ">
                                <h2 className="font-bold text-sm text-[#0f172a] mb-4 flex items-center gap-2">
                                    <ReceiptLongIcon className="w-4 h-4" />{' '}
                                    {t('Fees Breakdown')}
                                </h2>
                                <div className="flex flex-col gap-3">
                                    <div className="flex justify-between text-xs">
                                        <p className="text-[#64748b] ">
                                            {t('Open fee')}
                                        </p>

                                        <p className="font-medium text-slate-900 ">
                                            {formatDisplay(item.openFees, {
                                                decimalToShow: 2,
                                            })}{' '}
                                            WLD
                                        </p>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <p className="text-[#64748b] ">
                                            {t('Funding fee')}
                                        </p>

                                        <p className="font-medium text-slate-900 ">
                                            {formatDisplay(item.fundingFees, {
                                                decimalToShow: 2,
                                            })}{' '}
                                            WLD
                                        </p>
                                    </div>

                                    <div className="flex justify-between text-xs">
                                        <p className="text-[#64748b] ">
                                            {t('Rollover fee')}
                                        </p>

                                        <p className="font-medium text-slate-900 ">
                                            {formatDisplay(item.rolloverFees, {
                                                decimalToShow: 2,
                                            })}{' '}
                                            WLD
                                        </p>
                                    </div>

                                    <div className="flex justify-between text-xs">
                                        <p className="text-[#64748b] ">
                                            {t('Close fee')}
                                        </p>

                                        <p className="font-medium text-slate-900 ">
                                            {formatDisplay(item.closeFees, {
                                                decimalToShow: 2,
                                            })}{' '}
                                            WLD
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                className={clsx(
                                    'relative overflow-hidden rounded-2xl p-6 text-center mb-2 border bg-gradient-to-b to-transparent',
                                    {
                                        'border-[#10b981]/20 from-[#10b981]/5':
                                            item.pnlNumber >= 0,
                                        'border-[#ef4444]/20 from-[#ef4444]/5':
                                            item.pnlNumber < 0,
                                    }
                                )}
                            >
                                <div className="relative z-10">
                                    <div
                                        className={clsx(
                                            'font-semibold text-sm mb-1',
                                            {
                                                'text-[#ef4444] ':
                                                    item.pnlNumber < 0,
                                                'text-[#10b981] ':
                                                    item.pnlNumber >= 0,
                                            }
                                        )}
                                    >
                                        {t('Realized PNL')}
                                    </div>
                                    <div className="flex items-baseline justify-center gap-2">
                                        <span
                                            className={clsx(
                                                'text-2xl font-bold',
                                                {
                                                    'text-[#ef4444]':
                                                        item.pnlNumber < 0,
                                                    'text-[#10b981]':
                                                        item.pnlNumber >= 0,
                                                }
                                            )}
                                        >
                                            {item.pnlNumber > 0 ? '+' : ''}
                                            {formatDisplay(item.pnlNumber, {
                                                decimalToShow: 2,
                                            })}{' '}
                                            WLD
                                        </span>
                                        <span
                                            className={clsx(
                                                'text-sm font-medium',
                                                {
                                                    'text-[#ef4444]/80':
                                                        item.pnlNumber < 0,
                                                    'text-[#10b981]/80':
                                                        item.pnlNumber >= 0,
                                                }
                                            )}
                                        >
                                            ({percentage(item.pnlPercent)})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-x-2">
                                {/* <button
                                    onClick={() => setIsOpenSharePnl(true)}
                                    className="flex-1 bg-white  border border-slate-200 text-slate-700  py-3 rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 hover:bg-slate-50 active:scale-[0.98] transition-all shadow-sm"
                                >
                                    <TrendingUpIcon className="w-5 h-5" />
                                    <p className="text-[#808080] text-sm">
                                        {t('Flex')}
                                    </p>
                                </button> */}

                                {!isClose && (
                                    <button
                                        data-action="ReopenOrder"
                                        disabled={isLoading}
                                        onClick={() =>
                                            handleReopenOrder(item.buy)
                                        }
                                        className="flex-1 bg-white  border border-slate-200 text-slate-700  py-3 rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 hover:bg-slate-50 active:scale-[0.98] transition-all shadow-sm"
                                    >
                                        {isLoading ? (
                                            <Spinner className="w-4 h-4" />
                                        ) : (
                                            <div className="flex items-center gap-x-1">
                                                <CachedIcon w-5 h-5 />
                                                <p className="text-[#808080] text-sm">
                                                    {t('ReOpen')}
                                                </p>
                                            </div>
                                        )}
                                    </button>
                                )}

                                {/* <Link
                                    to={'/earn'}
                                    onClick={() => setIsOpenSharePnl(true)}
                                    className="flex items-center justify-center flex-1 border gap-x-2 rounded-2xl py-1 border-[#191919]"
                                >
                                    <IconEarn className="text-white fill-[#808080]" />
                                    <p className="text-[#808080] text-sm">
                                        {t('Earn')}
                                    </p>
                                </Link> */}
                            </div>
                        </div>
                        {openSharePnl && (
                            <SharePnl
                                isOpen={openSharePnl}
                                item={item}
                                onClose={() => setIsOpenSharePnl(false)}
                            />
                        )}
                    </div>
                </Sheet.Content>
            </Sheet.Container>

            <Sheet.Backdrop className=" cursor-pointer" onTap={onClose} />
        </Sheet>
    );
}
